import {
  getSelectedMeasureNamesFromFilters,
  measureBandMatchesFrequencyFilter,
  readTemplateCellFilterValues,
} from "./template-filters";

export interface ITemplateMeasureMetadata {
  name: string;
  idPoint: number;
  pointName: string;
  rangeType: number | null;
  units: number | null;
  presentation: number | null;
  param1: number | null;
  param2: number | null;
  param3: number | null;
}

export interface ITemplateMeasureRow {
  key: string;
  name: string;
  pointNames: string[];
  metadata: {
    rangeType: number | null;
    units: number | null;
    presentation: number | null;
    param1: number | null;
    param2: number | null;
    param3: number | null;
  };
}

const normalizeNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const buildMetadataKey = (metadata: ITemplateMeasureRow["metadata"]): string =>
  [
    metadata.rangeType ?? "x",
    metadata.units ?? "x",
    metadata.presentation ?? "x",
    metadata.param1 ?? "x",
    metadata.param2 ?? "x",
    metadata.param3 ?? "x",
  ].join(":");

export const groupTemplateMeasureRows = (measures: readonly ITemplateMeasureMetadata[]): ITemplateMeasureRow[] => {
  const grouped = new Map<string, ITemplateMeasureRow>();

  measures.forEach(measure => {
    const metadata = {
      rangeType: measure.rangeType,
      units: measure.units,
      presentation: measure.presentation,
      param1: measure.param1,
      param2: measure.param2,
      param3: measure.param3,
    };
    const key = `${measure.name}::${buildMetadataKey(metadata)}`;
    const existing = grouped.get(key);

    if (existing) {
      if (!existing.pointNames.includes(measure.pointName)) {
        existing.pointNames.push(measure.pointName);
      }
      return;
    }

    grouped.set(key, {
      key,
      name: measure.name,
      pointNames: [measure.pointName],
      metadata,
    });
  });

  return Array.from(grouped.values()).sort((left, right) => left.name.localeCompare(right.name));
};

export const buildMeasureRowLabel = (row: ITemplateMeasureRow): string => {
  if (!row.pointNames.length) {
    return row.name;
  }

  return `${row.name} (${row.pointNames.join(", ")})`;
};

export const buildFiltersFromMeasureRows = (
  rows: readonly ITemplateMeasureRow[],
  selectedKeys: readonly string[],
): Record<string, unknown> => {
  const selectedRows = rows.filter(row => selectedKeys.includes(row.key));

  if (!selectedRows.length) {
    return {
      rangeType: null,
      units: null,
      presentation: null,
      param1: null,
      param2: null,
      param3: null,
    };
  }

  const metadata = selectedRows[0].metadata;
  const selectedNames = Array.from(new Set(selectedRows.map(row => row.name)));

  return {
    rangeType: metadata.rangeType,
    units: metadata.units,
    presentation: metadata.presentation,
    param1: metadata.param1,
    param2: metadata.param2,
    param3: metadata.param3,
    ...(selectedNames.length === 1 ? { Name: selectedNames[0] } : { Name: selectedNames }),
  };
};

export const getSelectedMeasureRowKeys = (
  rows: readonly ITemplateMeasureRow[],
  filters: Record<string, unknown>,
): string[] => {
  const selectedNames = new Set(getSelectedMeasureNamesFromFilters(filters));
  const filterValues = readTemplateCellFilterValues(filters);

  if (!selectedNames.size) {
    return [];
  }

  return rows
    .filter(row => {
      if (!selectedNames.has(row.name)) {
        return false;
      }

      return (
        normalizeNumber(filterValues.rangeType) === row.metadata.rangeType &&
        normalizeNumber(filterValues.units) === row.metadata.units &&
        normalizeNumber(filterValues.presentation) === row.metadata.presentation &&
        measureBandMatchesFrequencyFilter(row.metadata.param1, row.metadata.param2, filters) &&
        normalizeNumber(filterValues.param3) === row.metadata.param3
      );
    })
    .map(row => row.key);
};
